import { useEffect, useRef } from 'react';
import { useAppAuth } from '@/lib/useAppAuth';
import { useKycStatus } from './useKyc';
import { openKycActionGateImperative } from '@/store/kycActionGateStore';

/**
 * Proactive action-verification gate. On load (and whenever the status query
 * refreshes — e.g. after a `kyc.action_status_changed` realtime invalidation,
 * which fires on an admin trigger) open the action popup for the first
 * unfinished action check, so an admin-triggered action surfaces immediately
 * instead of waiting for the player to find it under Settings.
 *
 * Defers to identity: while a HARD identity flow is still unsatisfied we don't
 * auto-prompt an action — an action can't run before the identity applicant
 * exists anyway, and the identity gate is reactive (it opens via the 403
 * KYC_REQUIRED interceptor when the user reaches a guarded action), so there's
 * nothing to stack on top of. Opens at most once per mount so dismissing isn't
 * immediately undone; because the guard only latches once it *opens*, a later
 * status refresh that introduces a new startable action still triggers it.
 */
export function useKycActionAutoGate(): void {
  const { isAuthenticated } = useAppAuth();
  const { data } = useKycStatus(isAuthenticated);
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    // Don't auto-prompt an action while identity is unsatisfied — the action
    // can't run yet, and the identity gate opens reactively on a guarded call.
    const identityPending = data?.verifications?.some((v) => v.enforcement === 'HARD' && v.status !== 'APPROVED');
    if (identityPending) return;
    // Admin-triggered actions start PENDING; a RED+RETRY review also returns to
    // PENDING — both want the player back in the SDK, same as the Settings list.
    const startable = data?.actions?.find((a) => a.status === 'NOT_STARTED' || a.status === 'PENDING');
    if (startable) {
      openedRef.current = true;
      openKycActionGateImperative(startable.externalActionId);
    }
  }, [data]);
}
